import { useId } from "react";
import { Camera, ImagePlus, Loader2, Pencil } from "lucide-react";
import { ffGradient } from "./FullFrameLogo";

/**
 * Robust upload buttons using <label htmlFor=…> + <input type=file>.
 *
 * Why a label and not Button.onClick → ref.click()?
 *   On some browsers (and inside Shadcn’s composed Buttons) a programmatic
 *   .click() on a hidden file input was reported by the user as "doing
 *   nothing". A native <label htmlFor> is the most compatible pattern that
 *   ALWAYS opens the file picker, including on iOS Safari.
 *
 * Renders three controls:
 *   - "צלם / סרוק"   → opens the camera (mobile only, falls back to picker)
 *   - "העלה ידנית"   → opens the file picker (gallery / file system)
 *   - "הזן ידנית"    → calls onManual() to insert an EMPTY row the user can
 *                      fill row-by-row. Optional — only rendered when an
 *                      `onManual` prop is provided.
 *
 * Both pickers call `onPick(file)` with the selected File.
 * `busy` shows a spinner inside the active control.
 */
export function UploadButtons({ onPick, onManual, busy, idPrefix = "ff", testidPrefix = "ff", size = "md" }) {
  // useId guarantees unique input ids per usage so duplicate UploadButtons on
  // the same page don't collide.
  const uid = useId().replace(/[:]/g, "");
  const camId = `${idPrefix}-cam-${uid}`;
  const galId = `${idPrefix}-gal-${uid}`;

  const handle = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onPick(file);
    // Reset so picking the same file twice still fires onChange.
    e.target.value = "";
  };

  const padY = size === "sm" ? "py-2" : "py-2.5";
  const textSize = size === "sm" ? "text-sm" : "text-base";

  return (
    <div className="flex items-center gap-1.5 flex-nowrap whitespace-nowrap">
      <input id={camId} type="file" accept="image/*" capture="environment" className="sr-only" onChange={handle} disabled={busy} data-testid={`${testidPrefix}-camera-input`} />
      <input id={galId} type="file" accept="image/*" className="sr-only" onChange={handle} disabled={busy} data-testid={`${testidPrefix}-gallery-input`} />

      <label
        htmlFor={camId}
        aria-disabled={busy}
        className={`inline-flex cursor-pointer items-center justify-center gap-1 rounded-md ${padY} px-2.5 ${textSize} font-bold text-white ${ffGradient} hover:opacity-90 transition shadow-md shadow-pink-500/20 ${busy ? "opacity-50 pointer-events-none" : ""}`}
        data-testid={`${testidPrefix}-camera-btn`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Camera className="h-4 w-4" />צלם</>}
      </label>

      <label
        htmlFor={galId}
        aria-disabled={busy}
        className={`inline-flex cursor-pointer items-center justify-center gap-1 rounded-md ${padY} px-2.5 ${textSize} font-bold text-white border border-white/15 bg-white/10 hover:bg-white/15 hover:border-pink-500/40 transition ${busy ? "opacity-50 pointer-events-none" : ""}`}
        data-testid={`${testidPrefix}-upload-btn`}
      >
        <ImagePlus className="h-4 w-4" />
        העלה תמונה
      </label>

      {onManual && (
        <button
          type="button"
          onClick={onManual}
          disabled={busy}
          className={`inline-flex items-center justify-center gap-1 rounded-md ${padY} px-2.5 ${textSize} font-bold text-pink-200 border border-pink-500/40 bg-pink-500/10 hover:bg-pink-500/20 hover:border-pink-500/60 transition ${busy ? "opacity-50 pointer-events-none" : ""}`}
          data-testid={`${testidPrefix}-manual-btn`}
        >
          <Pencil className="h-4 w-4" />
          הזן ידנית
        </button>
      )}
    </div>
  );
}
